import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { industryData as fullIndustryData } from '../data/industryDataComplete';
import { ReportModal, MailSettings, CalculationsReference, AgentTable, BenchmarkManager } from './admin';

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(''); // YYYY-MM-DD
  const [toDate, setToDate] = useState('');     // YYYY-MM-DD
  const [searchAllJson, setSearchAllJson] = useState(false);
  const [customPrefix, setCustomPrefix] = useState(''); // e.g., submissions/
  const [serverNote, setServerNote] = useState(null);
  const [serverDetails, setServerDetails] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  // Admin Settings state (tabs only - mail settings moved to MailSettings component)
  const [activeTab, setActiveTab] = useState('results'); // 'results' | 'leads' | 'pdf' | 'settings'
  const [settingsSubTab, setSettingsSubTab] = useState('mail'); // 'users' | 'mail' | 'industries' | 'agents' | 'calculations' | 'debug'
  // Deletion state
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [actionMsg, setActionMsg] = useState('');
  // Modal for report preview
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLead, setModalLead] = useState(null);
  const [modalMsg, setModalMsg] = useState('');
  // PDF/Reports Preview state
  const [selectedPdfRunId, setSelectedPdfRunId] = useState(null);
  const [selectedPdfData, setSelectedPdfData] = useState(null);
  const navigate = useNavigate();

  // Industries Admin state
  const [indSource, setIndSource] = useState('live'); // 'live' | 'draft'
  const [indSection, setIndSection] = useState('catalog'); // 'catalog' | 'tasks' | 'rpe' | 'admin'
  const [indData, setIndData] = useState({ catalog: [], tasks: {}, rpe: {}, admin: {} });
  const [indEditor, setIndEditor] = useState('');
  const [indMsg, setIndMsg] = useState('');
  const [indBusy, setIndBusy] = useState(false);
  const indFileInputRef = React.useRef(null);
  // Tasks editor working state
  const [indTasksWorking, setIndTasksWorking] = useState({});
  const [selectedIndustryId, setSelectedIndustryId] = useState('');
  // Canonical industry options (match prospect form)
  const industryOptions = useMemo(() => {
    try {
      return Array.isArray(fullIndustryData)
        ? fullIndustryData.map(i => ({ id: i.id, name: i.name }))
        : [];
    } catch { return []; }
  }, []);
  // Inline forms for Agents editing
  const [newAgent, setNewAgent] = useState({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  const [editAgentIdx, setEditAgentIdx] = useState(-1);
  const [editAgentObj, setEditAgentObj] = useState({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  const [newGeneralAgent, setNewGeneralAgent] = useState({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  const [editGenIdx, setEditGenIdx] = useState(-1);
  const [editGenObj, setEditGenObj] = useState({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });

  // Fallback: load industries from client src/data when blobs are empty (useful in dev before first publish)
  const loadClientIndustryDefaults = async () => {
    try {
      const modData = await import('../data/industryData');
      const catalog = Array.isArray(modData?.industryData) ? modData.industryData : [];
      const admin = modData?.adminWasteRates && typeof modData.adminWasteRates === 'object' ? modData.adminWasteRates : {};
      let tasks = {};
      try {
        const tmod = await import('../data/industryTasks');
        const localTasks = tmod?.industryTasks || tmod?.default || {};
        const localGA = Array.isArray(tmod?.generalAgents) ? tmod.generalAgents : [];
        // Attach generalAgents at top-level for editing convenience
        tasks = { ...localTasks, generalAgents: localGA };
      } catch {}
      let rpe = {};
      try { const rmod = await import('../data/industryRPE'); rpe = rmod?.industryRPE || rmod?.default || {}; } catch {}
      const merged = { catalog, tasks, rpe, admin };
      setIndData(merged);
      setIndTasksWorking(tasks || {});
      if (!selectedIndustryId) {
        const firstId = industryOptions.length > 0 ? industryOptions[0].id : ((Array.isArray(catalog) && catalog.length > 0) ? (catalog[0].id || '') : '');
        if (firstId) setSelectedIndustryId(firstId);
      }
      const current = indSection === 'catalog' ? merged.catalog : indSection === 'tasks' ? merged.tasks : indSection === 'rpe' ? merged.rpe : merged.admin;
      setIndEditor(JSON.stringify(current ?? (indSection==='catalog'?[]:{}), null, 2));
      setIndMsg('Loaded industries from local defaults (src/data). Save Draft to persist to Blobs.');
    } catch (e) {
      setIndMsg(`Failed to load industries defaults: ${String(e?.message || e)}`);
    }
  };
  // General Agents helpers
  const getGeneralAgents = () => {
    const t = indTasksWorking || {};
    return Array.isArray(t.generalAgents) ? t.generalAgents : [];
  };
  const setGeneralAgentsArr = (arr) => {
    setIndTasksWorking(prev => ({ ...(prev||{}), generalAgents: arr }));
  };
  const addGeneralAgentInline = () => {
    const label = (newGeneralAgent.label || '').trim();
    if (!label) { setIndMsg('General Agent Name is required'); return; }
    const fte = Number(newGeneralAgent.timeSavedFTE);
    const impact = Number(newGeneralAgent.costImpactRate);
    const score = newGeneralAgent.score === '' ? undefined : Number(newGeneralAgent.score);
    const next = [...getGeneralAgents(), { label, timeSavedFTE: isFinite(fte)? fte:0, costImpactRate: isFinite(impact)? impact:0, ...(isFinite(score)? {score}: {}) }];
    setGeneralAgentsArr(next);
    setNewGeneralAgent({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  };
  const beginEditGeneralAgent = (idx) => {
    const a = getGeneralAgents()[idx];
    if (!a) return;
    setEditGenIdx(idx);
    setEditGenObj({ label: a.label || '', timeSavedFTE: String(a.timeSavedFTE ?? ''), costImpactRate: String(a.costImpactRate ?? ''), score: a.score != null ? String(a.score) : '' });
  };
  const saveEditGeneralAgent = () => {
    if (editGenIdx < 0) return;
    const label = (editGenObj.label || '').trim();
    if (!label) { setIndMsg('General Agent Name is required'); return; }
    const fte = Number(editGenObj.timeSavedFTE);
    const impact = Number(editGenObj.costImpactRate);
    const score = editGenObj.score === '' ? undefined : Number(editGenObj.score);
    const arr = [...getGeneralAgents()];
    arr[editGenIdx] = { label, timeSavedFTE: isFinite(fte)? fte:0, costImpactRate: isFinite(impact)? impact:0, ...(isFinite(score)? {score}: {}) };
    setGeneralAgentsArr(arr);
    setEditGenIdx(-1);
    setEditGenObj({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  };
  const cancelEditGeneralAgent = () => {
    setEditGenIdx(-1);
    setEditGenObj({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  };
  const deleteGeneralAgentSimple = (idx) => {
    const arr = getGeneralAgents().filter((_,i)=>i!==idx);
    setGeneralAgentsArr(arr);
  };

  const fetchResults = async (opts = {}) => {
    const token = localStorage.getItem('roi_admin_token');
    if (!token) {
      navigate('/admin-login');
      return;
    }
    setLoading(true);
    setError(null);
    setServerNote(null);
    setServerDetails(null);
    setDebugInfo(null);
    try {
      const params = new URLSearchParams();
      const useFrom = typeof opts.from === 'string' ? opts.from : fromDate;
      const useTo = typeof opts.to === 'string' ? opts.to : toDate;
      const useAll = typeof opts.alljson === 'boolean' ? opts.alljson : searchAllJson;
      // Default prefixes by tab
      // - Results: runs/ (anonymous run snapshots)
      // - Leads:   leads/ (contact submissions)
      // - PDF/Reports: runs/ (same as results, for PDF preview)
      const defaultPrefix = activeTab === 'leads' ? 'leads/' : 'runs/';
      const usePrefix = typeof opts.prefix === 'string' ? opts.prefix : (customPrefix || defaultPrefix);
      const useDebug = typeof opts.debug === 'boolean' ? opts.debug : showDebug;
      if (useFrom) params.set('from', useFrom);
      if (useTo) params.set('to', useTo);
      if (useAll) params.set('mode', 'alljson');
      if (usePrefix) params.set('prefix', usePrefix);
      if (useDebug) params.set('debug', '1');
      const url = `/.netlify/functions/list-results${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Surface server-provided details and diagnostics even on error
        setItems([]);
        if (data?.note) setServerNote(data.note);
        if (data?.details) setServerDetails(data.details);
        if (data?.debug) setDebugInfo(data.debug);
        setError(String(data?.error || `Request failed (${res.status})`));
        return;
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      if (data?.note) setServerNote(data.note);
      if (data?.details) setServerDetails(data.details);
      if (data?.debug) setDebugInfo(data.debug);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  // Persist and load Debug/Search flags
  useEffect(() => {
    try {
      const debug = localStorage.getItem('roi_admin_show_debug');
      const alljson = localStorage.getItem('roi_admin_search_alljson');
      if (debug != null) setShowDebug(debug === '1');
      if (alljson != null) setSearchAllJson(alljson === '1');
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try { localStorage.setItem('roi_admin_show_debug', showDebug ? '1' : '0'); } catch {}
  }, [showDebug]);
  useEffect(() => {
    try { localStorage.setItem('roi_admin_search_alljson', searchAllJson ? '1' : '0'); } catch {}
  }, [searchAllJson]);

  const toggleSelect = (key, checked) => {
    setSelectedKeys(prev => {
      const n = new Set(prev);
      if (checked) n.add(key); else n.delete(key);
      return n;
    });
  };

  const deleteSelected = async () => {
    try {
      setActionMsg('');
      const keys = Array.from(selectedKeys);
      if (keys.length === 0) return;
      const includesReports = keys.some(k => k.startsWith('reports/'));
      let proceed = window.confirm(`Delete ${keys.length} entr${keys.length===1?'y':'ies'} from storage? This cannot be undone.${includesReports ? '\n\nIncludes items under reports/. You will be asked for an extra confirmation.' : ''}`);
      if (!proceed) return;
      const headers = { 'Content-Type': 'application/json' };
      if (includesReports) {
        const phrase = window.prompt('Type DELETE REPORTS to confirm deleting items under reports/');
        if (!phrase || phrase.trim().toUpperCase() !== 'DELETE REPORTS') {
          setActionMsg('Deletion canceled: reports confirmation not provided.');
          return;
        }
        headers['X-Confirm-Reports'] = 'DELETE REPORTS';
      }
      const token = localStorage.getItem('roi_admin_token');
      if (!token) throw new Error('Not authenticated');
      const res = await fetch('/.netlify/functions/delete-results', {
        method: 'POST',
        headers: { ...headers, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ keys })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) {
        throw new Error(data?.error || data?.details || 'Delete failed');
      }
      const deleted = Array.isArray(data.deleted) ? new Set(data.deleted) : new Set(keys);
      setItems(prev => prev.filter(it => !deleted.has(it.key)));
      setSelectedKeys(new Set());
      setActionMsg(`Deleted ${deleted.size} entr${deleted.size===1?'y':'ies'}.`);
    } catch (e) {
      setActionMsg(String(e?.message || e));
    }
  };

  useEffect(() => {
    // On first load, default range: last 30 days (UTC) through today
    const pad = (n) => String(n).padStart(2, '0');
    const today = new Date();
    const to = `${today.getUTCFullYear()}-${pad(today.getUTCMonth()+1)}-${pad(today.getUTCDate())}`;
    const d30 = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000); // inclusive of today => 30 days
    const from = `${d30.getUTCFullYear()}-${pad(d30.getUTCMonth()+1)}-${pad(d30.getUTCDate())}`;
    if (!fromDate) setFromDate(from);
    if (!toDate) setToDate(to);
    // Fetch using explicit params so we don't race state updates
    fetchResults({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Refetch when switching tabs to apply default prefix
  useEffect(() => {
    if (activeTab === 'settings' || activeTab === 'benchmarks') return;
    // Clear any selection when switching tabs to avoid accidental cross-tab deletes
    setSelectedKeys(new Set());
    if (fromDate && toDate) fetchResults({ from: fromDate, to: toDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Mail settings loading moved to MailSettings component

  // Load Industries data when entering Industries or Agents tab, or switching source
  useEffect(() => {
    const run = async () => {
      if (activeTab !== 'settings' || (settingsSubTab !== 'industries' && settingsSubTab !== 'agents')) return;
      setIndMsg('');
      setIndBusy(true);
      try {
        const token = localStorage.getItem('roi_admin_token');
        if (!token) { navigate('/admin-login'); throw new Error('Not authenticated'); }
        const qs = `?source=${encodeURIComponent(indSource)}&_=${Date.now()}`;
        const res = await fetch(`/.netlify/functions/admin-get-industries${qs}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
        const data = await res.json().catch(()=>({}));
        if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to load industries');
        const d = data.data || { catalog: [], tasks: {}, rpe: {}, admin: {} };
        const hasCatalog = Array.isArray(d.catalog) && d.catalog.length > 0;
        const hasTasks = d.tasks && typeof d.tasks === 'object' && Object.keys(d.tasks).length > 0;
        const hasRpe = d.rpe && typeof d.rpe === 'object' && Object.keys(d.rpe).length > 0;
        const hasAdmin = d.admin && typeof d.admin === 'object' && Object.keys(d.admin).length > 0;
        if (!hasCatalog && !hasTasks && !hasRpe && !hasAdmin) {
          // Nothing in blobs; fallback to client defaults
          await loadClientIndustryDefaults();
          return;
        }
        // If catalog exists but tasks are missing, merge tasks from local defaults as a partial fallback
        if (hasCatalog && !hasTasks) {
          try {
            const tmod = await import('../data/industryTasks');
            const localTasks = tmod?.industryTasks || tmod?.default || {};
            const localGA = Array.isArray(tmod?.generalAgents) ? tmod.generalAgents : [];
            d.tasks = { ...localTasks, generalAgents: localGA };
            setIndMsg('Tasks not found in Blobs; loaded tasks from local defaults. Save Draft to persist.');
          } catch {}
        }
        // If tasks exist but generalAgents missing, backfill generalAgents from defaults
        if (d.tasks && !Array.isArray(d.tasks.generalAgents)) {
          try {
            const tmod = await import('../data/industryTasks');
            const localGA = Array.isArray(tmod?.generalAgents) ? tmod.generalAgents : [];
            d.tasks = { ...(d.tasks||{}), generalAgents: localGA };
          } catch {}
        }
        setIndData(d);
        setIndTasksWorking(d.tasks || {});
        if (!selectedIndustryId) {
          const firstId = industryOptions.length > 0 ? industryOptions[0].id : ((Array.isArray(d.catalog) && d.catalog.length > 0) ? (d.catalog[0].id || '') : '');
          if (firstId) setSelectedIndustryId(firstId);
        }
        const current = indSection === 'catalog' ? d.catalog : indSection === 'tasks' ? d.tasks : indSection === 'rpe' ? d.rpe : d.admin;
        setIndEditor(JSON.stringify(current ?? (indSection==='catalog'?[]:{}), null, 2));
        setIndMsg(`Loaded ${indSource} industries.`);
      } catch (e) {
        setIndMsg(String(e?.message || e));
      } finally {
        setIndBusy(false);
      }
    };
    run();
  }, [activeTab, settingsSubTab, indSource]);

  // When switching industries section, refresh editor text from loaded data
  useEffect(() => {
    if (activeTab !== 'settings' || settingsSubTab !== 'industries') return;
    try {
      const d = indData || {};
      const current = indSection === 'catalog' ? d.catalog : indSection === 'tasks' ? d.tasks : indSection === 'rpe' ? d.rpe : d.admin;
      setIndEditor(JSON.stringify(current ?? (indSection==='catalog'?[]:{}), null, 2));
    } catch {}
  }, [indSection]);

  // Backfill tasks for selected industry from local defaults if missing
  useEffect(() => {
    const maybeBackfill = async () => {
      if (!selectedIndustryId) return;
      const cur = agentsForSelected();
      if (Array.isArray(cur) && cur.length > 0) return;
      // Attempt to load from local defaults for just this industry
      try {
        const tmod = await import('../data/industryTasks');
        const localTasks = tmod?.industryTasks || tmod?.default || {};
        const entry = localTasks[selectedIndustryId];
        const arr = Array.isArray(entry) ? entry : (entry && Array.isArray(entry.agents) ? entry.agents : []);
        if (arr.length > 0) {
          setAgentsForSelected(arr);
          setIndMsg(`No agents in ${indSource} for ${selectedIndustryId}; loaded from local defaults. Save Draft (Tasks) to persist.`);
        }
      } catch {}
    };
    maybeBackfill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustryId]);

  // Helpers for Tasks editor
  const agentsForSelected = () => {
    const t = indTasksWorking || {};
    const entry = t[selectedIndustryId];
    // Support both shapes: legacy array or object with { agents }
    if (Array.isArray(entry)) return entry;
    if (entry && Array.isArray(entry.agents)) return entry.agents;
    return [];
  };
  const setAgentsForSelected = (arr) => {
    setIndTasksWorking(prev => {
      const cur = { ...(prev || {}) };
      const entry = cur[selectedIndustryId];
      if (entry && !Array.isArray(entry)) {
        cur[selectedIndustryId] = { ...entry, agents: arr };
      } else {
        // If legacy array shape, upgrade to object shape with agents
        cur[selectedIndustryId] = { agents: arr };
      }
      return cur;
    });
  };
  const addAgentInline = () => {
    const label = (newAgent.label || '').trim();
    if (!label) { setIndMsg('Agent Name is required'); return; }
    const fte = Number(newAgent.timeSavedFTE);
    const impact = Number(newAgent.costImpactRate);
    const score = newAgent.score === '' ? undefined : Number(newAgent.score);
    const entry = { label, timeSavedFTE: isFinite(fte)? fte:0, costImpactRate: isFinite(impact)? impact:0, ...(isFinite(score)? {score}: {}) };
    setAgentsForSelected([...agentsForSelected(), entry]);
    setNewAgent({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  };
  const beginEditAgent = (idx) => {
    const a = agentsForSelected()[idx];
    if (!a) return;
    setEditAgentIdx(idx);
    setEditAgentObj({ label: a.label || '', timeSavedFTE: String(a.timeSavedFTE ?? ''), costImpactRate: String(a.costImpactRate ?? ''), score: a.score != null ? String(a.score) : '' });
  };
  const saveEditAgent = () => {
    if (editAgentIdx < 0) return;
    const label = (editAgentObj.label || '').trim();
    if (!label) { setIndMsg('Agent Name is required'); return; }
    const fte = Number(editAgentObj.timeSavedFTE);
    const impact = Number(editAgentObj.costImpactRate);
    const score = editAgentObj.score === '' ? undefined : Number(editAgentObj.score);
    const arr = [...agentsForSelected()];
    arr[editAgentIdx] = { label, timeSavedFTE: isFinite(fte)? fte:0, costImpactRate: isFinite(impact)? impact:0, ...(isFinite(score)? {score}: {}) };
    setAgentsForSelected(arr);
    setEditAgentIdx(-1);
    setEditAgentObj({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  };
  const cancelEditAgent = () => {
    setEditAgentIdx(-1);
    setEditAgentObj({ label: '', timeSavedFTE: '', costImpactRate: '', score: '' });
  };
  const deleteAgentSimple = (idx) => {
    const arr = agentsForSelected().filter((_,i)=>i!==idx);
    setAgentsForSelected(arr);
  };
  const saveTasksDraft = async () => {
    try {
      const token = localStorage.getItem('roi_admin_token');
      if (!token) { navigate('/admin-login'); throw new Error('Not authenticated'); }
      const res = await fetch('/.netlify/functions/admin-save-industries', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ section: 'tasks', mode: 'draft', data: indTasksWorking || {} }) });
      const data = await res.json().catch(()=>({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to save tasks');
      setIndMsg('Saved draft (Tasks).');
      setIndSource('draft');
    } catch (e) { setIndMsg(String(e?.message || e)); }
  };

  const saveIndustrySection = async (mode='draft') => {
    setIndMsg('');
    setIndBusy(true);
    try {
      const token = localStorage.getItem('roi_admin_token');
      if (!token) { navigate('/admin-login'); throw new Error('Not authenticated'); }
      let parsed;
      try { parsed = JSON.parse(indEditor); } catch { throw new Error('Editor JSON is invalid'); }
      const body = { section: indSection, mode, data: parsed };
      const res = await fetch('/.netlify/functions/admin-save-industries', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      const data = await res.json().catch(()=>({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to save');
      setIndMsg(mode==='live' ? 'Saved to live.' : 'Saved draft.');
      // Reload
      setIndSource(mode==='live' ? 'live' : indSource);
    } catch (e) {
      setIndMsg(String(e?.message || e));
    } finally {
      setIndBusy(false);
    }
  };

  const publishDraft = async () => {
    setIndMsg('');
    setIndBusy(true);
    try {
      const token = localStorage.getItem('roi_admin_token');
      if (!token) { navigate('/admin-login'); throw new Error('Not authenticated'); }
      const res = await fetch('/.netlify/functions/admin-publish-industries', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(()=>({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to publish');
      setIndMsg('Published draft to live.');
      setIndSource('live');
    } catch (e) {
      setIndMsg(String(e?.message || e));
    } finally { setIndBusy(false); }
  };

  const exportIndustries = async () => {
    setIndMsg('');
    try {
      const token = localStorage.getItem('roi_admin_token');
      if (!token) { navigate('/admin-login'); return; }
      const res = await fetch(`/.netlify/functions/admin-export-industries?source=${encodeURIComponent(indSource)}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
      const data = await res.json().catch(()=>({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Export failed');
      const blob = new Blob([JSON.stringify(data.export, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `industries-${indSource}-${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
      setIndMsg('Exported.');
    } catch (e) { setIndMsg(String(e?.message || e)); }
  };

  const onImportFile = async (evt) => {
    const file = evt.target.files && evt.target.files[0];
    if (!file) return;
    setIndBusy(true); setIndMsg('');
    try {
      const text = await file.text();
      let json; try { json = JSON.parse(text); } catch { throw new Error('Invalid JSON file'); }
      const token = localStorage.getItem('roi_admin_token');
      if (!token) { navigate('/admin-login'); throw new Error('Not authenticated'); }
      // Dry run
      let res = await fetch('/.netlify/functions/admin-import-industries', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ data: json, dryRun: true, mode: 'draft' }) });
      let data = await res.json().catch(()=>({}));
      if (!res.ok || !data?.ok) throw new Error((data && (data.error || data.errors?.join?.(', '))) || 'Validation failed');
      // Commit to draft
      res = await fetch('/.netlify/functions/admin-import-industries', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ data: json, dryRun: false, mode: 'draft' }) });
      data = await res.json().catch(()=>({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Import failed');
      setIndMsg('Imported to draft.');
      setIndSource('draft');
    } catch (e) {
      setIndMsg(String(e?.message || e));
    } finally { setIndBusy(false); evt.target.value = ''; }
  };

  // saveSettings moved to MailSettings component

  const logout = () => {
    localStorage.removeItem('roi_admin_token');
    navigate('/admin-login');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">ROI Admin</h1>
          <button onClick={logout} className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm">Log out</button>
        </div>
        {/* Tabs */}
        <div className="mb-4">
          <div className="inline-flex border rounded overflow-hidden">
            <button onClick={()=>setActiveTab('results')} className={`px-4 py-2 text-sm ${activeTab==='results' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>Results</button>
            <button onClick={()=>setActiveTab('leads')} className={`px-4 py-2 text-sm ${activeTab==='leads' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>Leads</button>
            <button onClick={()=>setActiveTab('pdf')} className={`px-4 py-2 text-sm ${activeTab==='pdf' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>Reports</button>
            <button onClick={()=>setActiveTab('benchmarks')} className={`px-4 py-2 text-sm ${activeTab==='benchmarks' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>Benchmarks</button>
            <button onClick={()=>setActiveTab('settings')} className={`px-4 py-2 text-sm ${activeTab==='settings' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}>Settings</button>
          </div>
        </div>
        {activeTab === 'settings' && (
          <div className="mb-4">
            <div className="inline-flex border rounded overflow-hidden">
              <button onClick={()=>setSettingsSubTab('users')} className={`px-3 py-1 text-sm ${settingsSubTab==='users' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>Users</button>
              <button onClick={()=>setSettingsSubTab('mail')} className={`px-3 py-1 text-sm ${settingsSubTab==='mail' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>Mail Settings</button>
              <button onClick={()=>setSettingsSubTab('industries')} className={`px-3 py-1 text-sm ${settingsSubTab==='industries' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>Industries</button>
              <button onClick={()=>setSettingsSubTab('agents')} className={`px-3 py-1 text-sm ${settingsSubTab==='agents' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>Agents</button>
              <button onClick={()=>setSettingsSubTab('calculations')} className={`px-3 py-1 text-sm ${settingsSubTab==='calculations' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>Calculations</button>
              <button onClick={()=>setSettingsSubTab('debug')} className={`px-3 py-1 text-sm ${settingsSubTab==='debug' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}>Debug</button>
            </div>
          </div>
        )}
        {activeTab !== 'settings' && activeTab !== 'benchmarks' && (
        /* Filters */
        <div className="bg-white rounded shadow p-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From (YYYY-MM-DD)</label>
              <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To (YYYY-MM-DD)</label>
              <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </div>
            {/* Debug/Search controls moved to Settings > Debug */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Custom prefix (optional)</label>
              <input type="text" placeholder="e.g., submissions/" value={customPrefix} onChange={e=>setCustomPrefix(e.target.value)} className="border rounded px-2 py-1 text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={fetchResults} className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">Search</button>
              <button onClick={() => { setFromDate(''); setToDate(''); setCustomPrefix(''); fetchResults(); }} className="px-3 py-2 bg-gray-200 rounded text-sm hover:bg-gray-300">Clear</button>
            </div>
          </div>
          {(serverNote || serverDetails) && (
            <div className="mt-3 text-xs text-gray-600">
              {serverNote && <p>Note: {serverNote}</p>}
              {serverDetails && <p className="break-words">Details: {serverDetails}</p>}
            </div>
          )}
          {showDebug && debugInfo && (
            <div className="mt-3 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
              <p className="font-semibold mb-1">Debug Diagnostics</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Mode:</strong> {String(debugInfo.mode || '')}</li>
                <li><strong>Custom Prefix:</strong> {String(debugInfo.customPrefix || '')}</li>
                <li><strong>Prefix Used:</strong> {String(debugInfo.prefixUsed || '(per-day)')}</li>
                <li><strong>Store Name:</strong> {String(debugInfo.storeName || '')}</li>
                <li><strong>Site ID Present:</strong> {String(debugInfo.siteIDPresent)}</li>
                <li><strong>Scanned Days:</strong> {String(debugInfo.scannedDays || 0)}</li>
                <li><strong>Scanned Entries (batches):</strong> {String(debugInfo.scannedEntries || 0)}</li>
                {typeof debugInfo.itemsLength === 'number' && (
                  <li><strong>Items Length:</strong> {String(debugInfo.itemsLength)}</li>
                )}
                {Array.isArray(debugInfo.sampleKeys) && debugInfo.sampleKeys.length > 0 && (
                  <li><strong>Sample Keys:</strong> <span className="break-all">{debugInfo.sampleKeys.join(', ')}</span></li>
                )}
              </ul>
              {Array.isArray(debugInfo.days) && debugInfo.days.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Per-day scan:</p>
                  <table className="mt-1 min-w-full text-[11px]">
                    <thead>
                      <tr className="text-gray-600">
                        <th className="text-left pr-2">Date</th>
                        <th className="text-left pr-2">Prefix Used</th>
                        <th className="text-left pr-2">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {debugInfo.days.map((d, i) => (
                        <tr key={i} className="align-top">
                          <td className="pr-2">{d.date}</td>
                          <td className="pr-2 break-all">{d.prefixUsed}</td>
                          <td className="pr-2">{String(d.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          </div>
        )}
        {/* Reports Tab - Split view with list, details, and preview */}
        {activeTab === 'pdf' && (
          <div className="flex gap-4" style={{ height: 'calc(100vh - 280px)' }}>
            {/* Left: Results List */}
            <div className="w-1/4 bg-white rounded shadow overflow-hidden flex flex-col">
              <div className="p-3 border-b bg-gray-50">
                <h3 className="font-semibold text-sm">Select a Report</h3>
                <p className="text-xs text-gray-500 mt-1">{items.length} results found</p>
              </div>
              <div className="flex-1 overflow-auto">
                {loading && <div className="p-4 text-sm text-gray-500">Loading...</div>}
                {!loading && items.length === 0 && <div className="p-4 text-sm text-gray-500">No results found. Try adjusting the date range.</div>}
                {!loading && items.map((it, idx) => {
                  const d = it.json || {};
                  const fd = d.formData || {};
                  const ci = d.contactInfo || {};
                  const runId = d.runId || it.key?.split('/').pop()?.replace('.json', '') || '';
                  const isSelected = selectedPdfRunId === runId;
                  // Get timestamp from various possible locations
                  const ts = d._serverTimestamp || d.timestamp || it.uploadedAt || '';
                  // Get display name - try company, then industry, then derive from key
                  const displayName = ci.company || fd.industry || runId.substring(0, 15) || '-';
                  // Get savings from various possible locations
                  const savings = d.potentialSavings?.high || d.savings?.potentialAnnualSavingsHigh || null;
                  return (
                    <div 
                      key={it.key || idx}
                      onClick={() => { setSelectedPdfRunId(runId); setSelectedPdfData(d); }}
                      className={`p-3 border-b cursor-pointer hover:bg-blue-50 transition-colors ${isSelected ? 'bg-blue-100 border-l-4 border-l-blue-600' : ''}`}
                    >
                      <div className="text-xs text-gray-500">{ts ? new Date(ts).toLocaleString() : runId}</div>
                      <div className="font-medium text-sm">{displayName}</div>
                      {ci.name && <div className="text-xs text-gray-600">{ci.name}</div>}
                      {fd.industry && ci.company && <div className="text-xs text-gray-500">{fd.industry}</div>}
                      {savings && (
                        <div className="text-xs text-green-600 font-medium mt-1">
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(savings)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Middle: Details Panel */}
            <div className="w-1/4 bg-white rounded shadow overflow-hidden flex flex-col">
              <div className="p-3 border-b bg-gray-50">
                <h3 className="font-semibold text-sm">Details</h3>
              </div>
              <div className="flex-1 overflow-auto p-3">
                {selectedPdfRunId && selectedPdfData ? (() => {
                  // Extract data with fallbacks for different structures
                  const d = selectedPdfData;
                  const fd = d.formData || {};
                  const ci = d.contactInfo || {};
                  const client = d.client || {};
                  const ps = d.potentialSavings || {};
                  const savings = d.savings || {};
                  const ts = d._serverTimestamp || d.timestamp || '';
                  const hasContact = ci.name || ci.email || ci.company;
                  const hasForm = fd.industry || fd.employeeCount || fd.annualRevenue;
                  const hasSavings = ps.low || ps.high || savings.potentialAnnualSavingsLow;
                  const hasClient = client.ip || client.userAgent;
                  return (
                  <div className="space-y-4 text-xs">
                    {/* Always show Run ID first */}
                    <div>
                      <div className="font-semibold text-gray-700 mb-2 border-b pb-1">Report Info</div>
                      <div className="space-y-1">
                        <div><span className="text-gray-500">Run ID:</span> <span className="font-mono text-[10px] break-all">{selectedPdfRunId}</span></div>
                        {ts && <div><span className="text-gray-500">Date:</span> {new Date(ts).toLocaleString()}</div>}
                      </div>
                    </div>
                    {/* Contact Info */}
                    {hasContact && (
                      <div>
                        <div className="font-semibold text-gray-700 mb-2 border-b pb-1">Contact Info</div>
                        <div className="space-y-1">
                          {ci.name && <div><span className="text-gray-500">Name:</span> {ci.name}</div>}
                          {ci.email && <div><span className="text-gray-500">Email:</span> {ci.email}</div>}
                          {ci.phone && <div><span className="text-gray-500">Phone:</span> {ci.phone}</div>}
                          {ci.company && <div><span className="text-gray-500">Company:</span> {ci.company}</div>}
                          {ci.website && <div><span className="text-gray-500">Website:</span> {ci.website}</div>}
                        </div>
                      </div>
                    )}
                    {/* Form Data */}
                    {hasForm && (
                      <div>
                        <div className="font-semibold text-gray-700 mb-2 border-b pb-1">Form Inputs</div>
                        <div className="space-y-1">
                          {fd.industry && <div><span className="text-gray-500">Industry:</span> {fd.industry}</div>}
                          {fd.employeeCount && <div><span className="text-gray-500">Employees:</span> {fd.employeeCount?.toLocaleString()}</div>}
                          {fd.annualRevenue && <div><span className="text-gray-500">Revenue:</span> ${fd.annualRevenue?.toLocaleString()}</div>}
                          {fd.hourlyCost && <div><span className="text-gray-500">Hourly Cost:</span> ${fd.hourlyCost}</div>}
                          {fd.automationMaturity && <div><span className="text-gray-500">Automation:</span> {fd.automationMaturity}/10</div>}
                          {fd.processMaturity && <div><span className="text-gray-500">Process:</span> {fd.processMaturity}/10</div>}
                        </div>
                      </div>
                    )}
                    {/* Results */}
                    {hasSavings && (
                      <div>
                        <div className="font-semibold text-gray-700 mb-2 border-b pb-1">Calculated Results</div>
                        <div className="space-y-1">
                          <div><span className="text-gray-500">Savings:</span> <span className="text-green-600 font-medium">${(ps.low || savings.potentialAnnualSavingsLow || 0)?.toLocaleString()} - ${(ps.high || savings.potentialAnnualSavingsHigh || 0)?.toLocaleString()}</span></div>
                          {d.efficiencyScore && <div><span className="text-gray-500">Efficiency Score:</span> {d.efficiencyScore}</div>}
                          {d.agentImpact?.total && <div><span className="text-gray-500">Agent Impact:</span> ${d.agentImpact.total?.toLocaleString()}</div>}
                        </div>
                      </div>
                    )}
                    {/* Session Data */}
                    {hasClient && (
                      <div>
                        <div className="font-semibold text-gray-700 mb-2 border-b pb-1">Session Data</div>
                        <div className="space-y-1">
                          {client.ip && <div><span className="text-gray-500">IP:</span> {client.ip}</div>}
                          {client.userAgent && <div><span className="text-gray-500">Browser:</span> <span className="break-all">{client.userAgent.substring(0, 80)}...</span></div>}
                          {client.referer && <div><span className="text-gray-500">Referrer:</span> <span className="break-all">{client.referer}</span></div>}
                          {client.acceptLanguage && <div><span className="text-gray-500">Language:</span> {client.acceptLanguage}</div>}
                        </div>
                      </div>
                    )}
                    {/* AI Research & Qualification */}
                    {d.aiResearch && (
                      <div>
                        <div className="font-semibold text-gray-700 mb-2 border-b pb-1">🤖 AI Research</div>
                        <div className="space-y-1">
                          {d.aiResearch.company?.description && (
                            <div><span className="text-gray-500">Company:</span> <span className="text-[10px]">{d.aiResearch.company.description.substring(0, 100)}...</span></div>
                          )}
                          {d.aiResearch.person?.title && (
                            <div><span className="text-gray-500">Role:</span> {d.aiResearch.person.title}</div>
                          )}
                          {d.aiResearch.qualification && (
                            <>
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <span className="text-gray-500">Fit Score:</span>{' '}
                                <span className={`font-semibold ${d.aiResearch.qualification.companyFitScore >= 7 ? 'text-green-600' : d.aiResearch.qualification.companyFitScore >= 5 ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {d.aiResearch.qualification.companyFitScore}/10
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Priority:</span>{' '}
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.aiResearch.qualification.priorityLevel === 'hot' ? 'bg-red-100 text-red-700' : d.aiResearch.qualification.priorityLevel === 'warm' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {d.aiResearch.qualification.priorityLevel?.toUpperCase()}
                                </span>
                              </div>
                              {d.aiResearch.qualification.decisionMaker?.role && (
                                <div><span className="text-gray-500">DM Status:</span> {d.aiResearch.qualification.decisionMaker.role}</div>
                              )}
                            </>
                          )}
                          <div className="text-[10px] text-green-600 mt-1">✓ Research complete</div>
                        </div>
                      </div>
                    )}
                    {/* Show raw keys if no structured data */}
                    {!hasContact && !hasForm && !hasSavings && !d.aiResearch && (
                      <div>
                        <div className="font-semibold text-gray-700 mb-2 border-b pb-1">Raw Data Keys</div>
                        <div className="space-y-1 text-[10px] text-gray-500">
                          {Object.keys(d).slice(0, 10).map(k => <div key={k}>{k}</div>)}
                        </div>
                      </div>
                    )}
                    {/* Actions */}
                    <div className="pt-2 border-t space-y-2">
                      <a 
                        href={`/.netlify/functions/get-pdf-preview?runId=${encodeURIComponent(selectedPdfRunId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full text-center px-3 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                      >
                        Download / Print Report
                      </a>
                      <button 
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to delete this report? This cannot be undone.')) return;
                          try {
                            const authToken = localStorage.getItem('roi_admin_token');
                            if (!authToken) { alert('Not authenticated'); return; }
                            // Find the key for this runId
                            const item = items.find(it => (it.json?.runId || it.key?.split('/').pop()?.replace('.json', '')) === selectedPdfRunId);
                            if (!item?.key) { alert('Could not find item key'); return; }
                            const res = await fetch('/.netlify/functions/delete-results', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                              body: JSON.stringify({ keys: [item.key] })
                            });
                            if (res.ok) {
                              setSelectedPdfRunId(null);
                              setSelectedPdfData(null);
                              fetchResults();
                              setActionMsg('Report deleted successfully');
                            } else {
                              alert('Failed to delete report');
                            }
                          } catch (e) {
                            alert('Error deleting report: ' + e.message);
                          }
                        }}
                        className="block w-full text-center px-3 py-2 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                      >
                        Delete Report
                      </button>
                    </div>
                  </div>
                  );
                })() : (
                  <div className="text-gray-400 text-center mt-8">Select a report to view details</div>
                )}
              </div>
            </div>
            {/* Right: PDF Preview */}
            <div className="flex-1 bg-white rounded shadow overflow-hidden flex flex-col">
              <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-sm">Report Preview</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedPdfData?.contactInfo?.company || selectedPdfData?.formData?.industry || 'Select a report'}
                  </p>
                </div>
                {selectedPdfRunId && (
                  <a 
                    href={`/.netlify/functions/get-pdf-preview?runId=${encodeURIComponent(selectedPdfRunId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Open in new tab ↗
                  </a>
                )}
              </div>
              <div className="flex-1">
                {selectedPdfRunId ? (
                  <iframe 
                    key={selectedPdfRunId}
                    title="PDF Preview" 
                    src={`/.netlify/functions/get-pdf-preview?runId=${encodeURIComponent(selectedPdfRunId)}`}
                    className="w-full h-full border-0"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <div className="text-4xl mb-2">📄</div>
                      <div>Select a report to preview</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Benchmarks Tab */}
        {activeTab === 'benchmarks' && (
          <div className="bg-white rounded shadow p-4 mb-6">
            <BenchmarkManager />
          </div>
        )}
        {activeTab === 'settings' && (
          <div className="bg-white rounded shadow p-4 mb-6 max-w-3xl">
            {/* Mail Settings - extracted component */}
            {settingsSubTab === 'mail' && (
              <MailSettings showDebug={showDebug} />
            )}
            {settingsSubTab === 'industries' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold">Industries</h2>
                  {indBusy && <span className="text-xs text-gray-600">Working…</span>}
                  {indMsg && <span className="text-xs text-gray-700">{indMsg}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-700">Source</label>
                  <select value={indSource} onChange={e=>setIndSource(e.target.value)} className="border rounded px-2 py-1 text-sm">
                    <option value="live">Live</option>
                    <option value="draft">Draft</option>
                  </select>
                  <label className="ml-4 text-sm text-gray-700">Section</label>
                  <select value={indSection} onChange={e=>setIndSection(e.target.value)} className="border rounded px-2 py-1 text-sm">
                    <option value="catalog">Catalog</option>
                    <option value="rpe">RPE</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">JSON Editor</label>
                  <textarea value={indEditor} onChange={e=>setIndEditor(e.target.value)} className="w-full border rounded px-3 py-2 font-mono text-xs min-h-[280px]" spellCheck={false} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={()=>saveIndustrySection('draft')} className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded" disabled={indBusy}>Save Draft (Section)</button>
                  <button type="button" onClick={publishDraft} className="text-sm px-3 py-1.5 bg-green-600 text-white rounded" disabled={indBusy}>Publish Draft → Live</button>
                  <button type="button" onClick={exportIndustries} className="text-sm px-3 py-1.5 bg-gray-200 rounded" disabled={indBusy}>Export ({indSource})</button>
                  <input ref={indFileInputRef} type="file" accept="application/json" onChange={onImportFile} className="hidden" />
                  <button type="button" onClick={()=>indFileInputRef.current?.click()} className="text-sm px-3 py-1.5 bg-gray-200 rounded" disabled={indBusy}>Import → Draft</button>
                </div>
                <p className="text-xs text-gray-500">Tip: Save Draft updates only the selected section. Publish promotes all draft sections to live and snapshots previous live for rollback.</p>
              </div>
            )}
            {settingsSubTab === 'users' && (
              <div className="text-sm text-gray-700">Users settings coming soon.</div>
            )}
            
            {settingsSubTab === 'agents' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold">Agents</h2>
                  {indBusy && <span className="text-xs text-gray-600">Working…</span>}
                  {indMsg && <span className="text-xs text-gray-700">{indMsg}</span>}
                </div>
                <div className="text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="font-semibold mb-1">Field guidance</div>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Name</strong>: Plain-English agent label (e.g., “Invoice intake and 3-way match”).</li>
                    <li><strong>FTE Saved</strong>: Fraction of a full-time employee saved (0.0–1.0). Example values: 0.05 ≈ ~2 hrs/week; 0.10 ≈ ~4 hrs/week. Use conservative estimates.</li>
                    <li><strong>Cost Impact</strong>: Fraction of annual revenue or relevant cost affected (0.0–1.0). Typically small numbers (e.g., 0.0005–0.005). Represents expected annual cost reduction or revenue uplift attributable to this agent.</li>
                    <li><strong>Score</strong>: Optional weight (1–10) used for ranking/auto-select. Higher = more likely to include when using presets.</li>
                  </ul>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-700">Source</label>
                  <select value={indSource} onChange={e=>setIndSource(e.target.value)} className="border rounded px-2 py-1 text-sm">
                    <option value="live">Live</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="text-sm text-gray-700">Industry</label>
                    <select value={selectedIndustryId} onChange={e=>setSelectedIndustryId(e.target.value)} className="border rounded px-2 py-1 text-sm">
                      {industryOptions.map(it => (
                        <option key={it.id} value={it.id}>{it.name || it.id}</option>
                      ))}
                    </select>
                    <button type="button" onClick={saveTasksDraft} className="text-sm px-2 py-1 bg-green-600 text-white rounded">Save Draft (Tasks)</button>
                  </div>
                  {/* Industry-specific agents table - extracted component */}
                  <AgentTable
                    agents={agentsForSelected()}
                    editIdx={editAgentIdx}
                    editObj={editAgentObj}
                    setEditObj={setEditAgentObj}
                    newAgent={newAgent}
                    setNewAgent={setNewAgent}
                    onBeginEdit={beginEditAgent}
                    onSaveEdit={saveEditAgent}
                    onCancelEdit={cancelEditAgent}
                    onDelete={deleteAgentSimple}
                    onAdd={addAgentInline}
                    emptyMessage="No agents yet."
                  />
                  <hr className="my-2" />
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-medium">General Agents</h3>
                    <button type="button" onClick={saveTasksDraft} className="text-sm px-2 py-1 bg-green-600 text-white rounded">Save Draft (Tasks)</button>
                  </div>
                  {/* General agents table - extracted component */}
                  <AgentTable
                    agents={getGeneralAgents()}
                    editIdx={editGenIdx}
                    editObj={editGenObj}
                    setEditObj={setEditGenObj}
                    newAgent={newGeneralAgent}
                    setNewAgent={setNewGeneralAgent}
                    onBeginEdit={beginEditGeneralAgent}
                    onSaveEdit={saveEditGeneralAgent}
                    onCancelEdit={cancelEditGeneralAgent}
                    onDelete={deleteGeneralAgentSimple}
                    onAdd={addGeneralAgentInline}
                    emptyMessage="No general agents yet."
                  />
                </div>
              </div>
            )}
            {/* Calculations Reference - extracted component */}
            {settingsSubTab === 'calculations' && (
              <CalculationsReference />
            )}
          </div>
        )}
        {activeTab !== 'settings' && activeTab !== 'pdf' && loading && <p>Loading…</p>}
        {activeTab !== 'settings' && activeTab !== 'pdf' && error && <p className="text-red-600">{error}</p>}
        {activeTab !== 'settings' && activeTab !== 'pdf' && !loading && !error && (
          <div className="overflow-x-auto bg-white rounded shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  {showDebug && (
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                  )}
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                  {activeTab === 'results' && (
                    <>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employees</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Automation</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Process</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrer</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated Savings</th>
                    </>
                  )}
                  {activeTab === 'leads' && (
                    <>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrer</th>
                    </>
                  )}
                  {activeTab === 'reports' && (
                    <>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Key</th>
                    </>
                  )}
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={deleteSelected} disabled={selectedKeys.size===0} title="Delete selected" className={`inline-flex items-center gap-1 px-2 py-1 rounded ${selectedKeys.size? 'bg-red-600 text-white':'bg-gray-200 text-gray-500 cursor-not-allowed'}`}>
                      <span aria-hidden>🗑️</span>
                      <span>Delete</span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((it) => {
                  const d = it.data || {};
                  const deriveFromKey = (key) => {
                    const s = String(key || '');
                    // runs/YYYY/MM/DD/yyyymmdd-HHMMSS-xxxx.json
                    let m = s.match(/runs\/(\d{4})\/(\d{2})\/(\d{2})\/(\d{8})-(\d{6})-/);
                    if (m) {
                      const yyyy = m[1], mm = m[2], dd = m[3];
                      const hhmmss = m[5] || '000000';
                      return `${yyyy}-${mm}-${dd} ${hhmmss.slice(0,2)}:${hhmmss.slice(2,4)}:${hhmmss.slice(4,6)}`;
                    }
                    // reports/YYYY/MM/DD/<filename>
                    m = s.match(/reports\/(\d{4})\/(\d{2})\/(\d{2})\//);
                    if (m) {
                      const yyyy = m[1], mm = m[2], dd = m[3];
                      return `${yyyy}-${mm}-${dd}`;
                    }
                    // leads/YYYY/MM/DD/<filename>
                    m = s.match(/leads\/(\d{4})\/(\d{2})\/(\d{2})\//);
                    if (m) {
                      const yyyy = m[1], mm = m[2], dd = m[3];
                      return `${yyyy}-${mm}-${dd}`;
                    }
                    return '';
                  };
                  const when = d._serverTimestamp || d.timestamp || it.uploadedAt || deriveFromKey(it.key) || '';
                  const currency = d.formData?.currency || 'USD';
                  const fmtCurrency = (val) => {
                    if (val === null || val === undefined || val === '-') return '-';
                    if (typeof val === 'string' && /\$|,/.test(val)) return val; // already formatted
                    const num = Number(val);
                    if (!isNaN(num)) {
                      try {
                        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
                      } catch {
                        return String(val);
                      }
                    }
                    return String(val);
                  };
                  const rawSavings = (d.savings && (d.savings.estimatedAnnualSavingsFormatted ?? d.savings.estimatedAnnualSavings))
                    ?? (d.agentImpact && d.agentImpact.total)
                    ?? undefined;
                  const savings = rawSavings !== undefined ? fmtCurrency(rawSavings) : '-';
                  const revenue = d.formData?.annualRevenue !== undefined ? fmtCurrency(d.formData.annualRevenue) : '-';
                  const ip = d.client?.ip || '-';
                  const ref = d.client?.referer || d.client?.referrer || '-';
                  const name = d.contactInfo?.name || '-';
                  const email = d.contactInfo?.email || '-';
                  const deriveCompanyFromEmail = (em) => {
                    if (!em || typeof em !== 'string' || !em.includes('@')) return null;
                    const domain = em.split('@')[1] || '';
                    const host = domain.split(':')[0].split('/')[0];
                    const parts = host.split('.').filter(Boolean);
                    if (parts.length === 0) return null;
                    const core = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
                    if (!core || core.length < 2) return null;
                    return core.charAt(0).toUpperCase() + core.slice(1);
                  };
                  const company = d.contactInfo?.company 
                    || d.contactInfo?.organization 
                    || d.contactInfo?.org 
                    || d.formData?.company 
                    || d.snapshot?.formData?.company 
                    || deriveCompanyFromEmail(email)
                    || '-';
                  const leadReport = d.report || d.snapshot?.report || null;
                  const onRowClick = () => {
                    if (activeTab === 'results') {
                      // Show raw JSON for run snapshot with header fields
                      const whenStr = d._serverTimestamp || d.timestamp || deriveFromKey(it.key) || '-';
                      const ipAddr = d.client?.ip || '-';
                      const getHost = (url) => {
                        try { const u = new URL(url); return u.hostname; } catch { return null; }
                      };
                      const host = getHost(d.client?.referer || d.client?.referrer || '') || d.client?.rdns || d.client?.reverseDns || '';
                      const deriveCompanyFromHost = (h) => {
                        if (!h) return null;
                        const parts = String(h).split('.').filter(Boolean);
                        if (parts.length === 0) return null;
                        const core = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
                        if (!core || core.length < 2) return null;
                        return core.charAt(0).toUpperCase() + core.slice(1);
                      };
                      const org = d.client?.rdns || d.client?.reverseDns || deriveCompanyFromHost(host) || '-';
                      setModalLead({ name: '-', email: '-', company: '-', runId: d.runId || d.run_id || d.runID || null, key: it.key, view: 'json', json: d, headerWhen: whenStr, headerIp: ipAddr, headerOrg: org });
                      setModalMsg('');
                      setModalOpen(true);
                      return;
                    }
                    if (activeTab === 'leads') {
                      // Open by runId using lead snapshot to allow viewing/generating report
                      const candidateRunId = d.runId || d.run_id || d.runID || it.data?.runId || null;
                      setModalLead({ name, email, company, runId: candidateRunId, key: it.key, hasReport: false, report: leadReport || null, snapshot: d.snapshot || d });
                      setModalMsg('');
                      setModalOpen(true);
                      return;
                    }
                    if (activeTab === 'reports') {
                      // Open modal by report key directly
                      setModalLead({ name: '-', email: '-', company: '-', runId: null, key: it.key, hasReport: true, report: null, snapshot: null, reportKey: it.key });
                      setModalMsg('');
                      setModalOpen(true);
                    }
                  };
                  return (
                    <tr key={it.key} className={`hover:bg-gray-50 ${(activeTab==='reports' || activeTab==='leads' || activeTab==='results') ? 'cursor-pointer' : ''}`} onClick={onRowClick}>
                      {showDebug && (
                        <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500 break-all max-w-[280px]">{it.key}</td>
                      )}
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{when}</td>
                      {activeTab === 'results' && (
                        <>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{d.formData?.industry || '-'}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{d.formData?.employeeCount ?? '-'}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{revenue}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{d.formData?.automationMaturity ?? '-'}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{d.formData?.processMaturity ?? '-'}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{ip}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 break-all max-w-[280px]">{ref}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{savings}</td>
                        </>
                      )}
                      {activeTab === 'leads' && (
                        <>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{name}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 break-all">{email}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{company}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{ip}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 break-all max-w-[280px]">{ref}</td>
                        </>
                      )}
                      {activeTab === 'reports' && (
                        <>
                          <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-700 break-all">{it.key}</td>
                        </>
                      )}
                      <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                        <input type="checkbox" checked={selectedKeys.has(it.key)} onChange={(e)=>{e.stopPropagation(); toggleSelect(it.key, e.target.checked);}} />
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={showDebug ? 10 : 9}>No results yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
            {actionMsg && (
              <div className="px-4 py-2 text-sm text-gray-700">{actionMsg}</div>
            )}
          </div>
        )}

        {/* Modal for Report Preview - extracted component */}
        <ReportModal
          isOpen={modalOpen}
          lead={modalLead}
          message={modalMsg}
          onClose={() => { setModalOpen(false); setModalLead(null); }}
          onMessage={setModalMsg}
          onLeadUpdate={setModalLead}
        />
      </div>
    </div>
  );
}
